import moment from 'moment-timezone';

import type {
  NotificationContextPayload,
  NotificationType,
} from 'src/components/notifications/types';
import type {ReduxStore} from 'src/reducers';
import type {BaseHistory} from 'src/rerouter/history/base';
import {AnalyticsService} from 'src/analytics';
import {NOTIFICATIONS_EVENTS} from 'src/components/notifications/amplitude-events';
import {markNotificationAsRead} from 'src/action-creators/notifications';
import logger from 'src/utils/logger';
import {snake} from 'src/utils/camel-snake';


const BODY_TEMPLATE_PLACEHOLDER = /\{\{\s*([^}]+?)\s*\}\}/g;

export type InterpolatedNotificationBody = {
  resolvedBody: string;
  highlightValues: string[];
};

/**
 * Replaces `{{varName}}` in `bodyTemplate` using `context`.
 * Unknown keys keep the original placeholder.
 */
export function interpolateNotificationBodyTemplate(
  bodyTemplate: string | null,
  context: NotificationContextPayload,
): InterpolatedNotificationBody {
  if (!bodyTemplate) {
    return {resolvedBody: '', highlightValues: []};
  }

  const lookupContextValues: Record<string, string> =
    context != null
      ? {...(snake(context) as Record<string, string>), ...context}
      : {};

  const highlightValues: string[] = [];

  const resolvedBody = bodyTemplate.replace(
    BODY_TEMPLATE_PLACEHOLDER,
    (_, rawName: string) => {
      const key = rawName.trim();
      const value = lookupContextValues[key];
      if (value === undefined) {
        return `{{${key}}}`;
      }
      highlightValues.push(value);
      return value;
    },
  );

  return {resolvedBody, highlightValues};
}

export const groupNotificationsByDate = (
  timezone: string,
  notifications: NotificationType[],
) => {
  const tz = timezone || '';
  const todayKey = moment.tz(tz).startOf('day');
  const yesterdayKey = moment.tz(tz).subtract(1, 'day').startOf('day');

  const groups: Record<'today' | 'yesterday' | 'earlier', NotificationType[]> =
    {
      today: [],
      yesterday: [],
      earlier: [],
    };

  return notifications.reduce((groups, notification: NotificationType) => {
    const createdDate = moment(notification.createdAt).tz(tz);

    if (createdDate.isSame(todayKey, 'day')) {
      groups.today.push(notification);
    } else if (createdDate.isSame(yesterdayKey, 'day')) {
      groups.yesterday.push(notification);
    } else {
      groups.earlier.push(notification);
    }

    return groups;
  }, groups);
};

export const handleNotificationDeepLinkParams = (
  reduxStore: ReduxStore,
  history: BaseHistory,
) => {
  try {
    const {searchParams, pathname, hash} = history.location;
    const notificationId = searchParams.get('notificationId');
    const notificationType = searchParams.get('notificationType');

    if (!notificationId) {
      return;
    }

    AnalyticsService.track(NOTIFICATIONS_EVENTS.PUSH_NOTIFICATION_CLICKED, {
      notificationId,
      notificationType,
    });

    reduxStore.dispatch(markNotificationAsRead(notificationId, true));

    const cleanedParams = new URLSearchParams(searchParams);
    cleanedParams.delete('notificationId');
    cleanedParams.delete('notificationType');

    history.replace(
      {pathname, searchParams: cleanedParams, hash},
      {silent: true},
    );
  } catch (error) {
    logger.error('Error handling notification deep link params', error);
  }
};
